import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, MessageSquare, Trash2, Search } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import type { Conversation } from '@/schemas/chat'

interface ConversationSidebarProps {
  conversations: Conversation[]
  activeConversationId: string | null
  isLoading: boolean
  onNewConversation: () => void
  onSelectConversation: (id: string) => void
  onDeleteConversation: (id: string) => void
}

export function ConversationSidebar({
  conversations,
  activeConversationId,
  isLoading,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
}: ConversationSidebarProps) {
  const { t } = useTranslation('chat')
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const filtered = search
    ? (conversations ?? []).filter(
        (c) =>
          c.title?.toLowerCase().includes(search.toLowerCase()) ||
          c.agent_name?.toLowerCase().includes(search.toLowerCase())
      )
    : (conversations ?? [])

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return t('time.now', { ns: 'common' })
    if (diffMins < 60) return `${diffMins}min`
    if (diffHours < 24) return `${diffHours}h`
    return `${diffDays}d`
  }

  return (
    <Card variant="glass" className="w-80 shrink-0 flex flex-col rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-700/50">
        <Button
          onClick={onNewConversation}
          className="w-full rounded-xl bg-indigo-500 hover:bg-indigo-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('sidebar.newConversation')}
        </Button>

        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('sidebar.searchConversations')}
            className="pl-9 rounded-xl border-gray-700/50 bg-white/5"
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="space-y-2 p-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <MessageSquare className="h-8 w-8 text-gray-500 mb-3" />
            <p className="text-sm text-gray-400 text-center">
              {t('sidebar.noConversations')}
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              {t('sidebar.noConversationsDesc')}
            </p>
          </div>
        ) : (
          filtered.map((conversation) => {
            const isActive = conversation.id === activeConversationId
            return (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation.id)}
                className={`group w-full flex items-center gap-3 rounded-xl p-3 text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-500/20 border border-indigo-500/30'
                    : 'hover:bg-white/5 border border-transparent'
                }`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-200 truncate">
                    {conversation.title || conversation.agent_name || 'Nova conversa'}
                  </p>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-gray-500 truncate">
                      {conversation.agent_name}
                    </p>
                    <span className="text-xs text-gray-500 shrink-0 ml-2">
                      {formatTime(conversation.updated_at)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteTarget(conversation.id)
                  }}
                  className="opacity-0 group-hover:opacity-100 shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-red-500/10 hover:text-red-400 transition-all"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </button>
            )
          })
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        onConfirm={() => {
          if (deleteTarget) {
            onDeleteConversation(deleteTarget)
            setDeleteTarget(null)
          }
        }}
        title={t('sidebar.deleteConversation')}
        description={t('sidebar.confirmDelete')}
        confirmLabel={t('sidebar.deleteConversation')}
        confirmingLabel="..."
        confirmVariant="action-red"
        isProcessing={false}
      />
    </Card>
  )
}
